import {
  Controller,
  useFieldArray,
  type Control,
  type FieldErrors,
} from 'react-hook-form';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import { Button, Input, Label, ListBox, Select } from '@heroui/react';
import {
  AI_BILLING_DIMENSIONS,
  AI_BILLING_DIMENSION_LABELS,
  type AiBillingDimension,
} from '../constants';

export type InvokeUsageItemFormValues = {
  usage: AiBillingDimension;
  quantity: number;
  unit_price: number;
  amount: number;
};

export type InvokeLogFormWithUsageItems = {
  usageItems: InvokeUsageItemFormValues[];
};

const EMPTY_USAGE_ITEM: InvokeUsageItemFormValues = {
  usage: 'INPUT_TOKEN',
  quantity: 0,
  unit_price: 0,
  amount: 0,
};

interface AiInvokeUsageItemsEditorProps<T extends InvokeLogFormWithUsageItems> {
  control: Control<T>;
  errors?: FieldErrors<T>;
  onRowsChange?: () => void;
}

export function AiInvokeUsageItemsEditor<T extends InvokeLogFormWithUsageItems>({
  control,
  errors,
  onRowsChange,
}: AiInvokeUsageItemsEditorProps<T>) {
  const { fields, append, remove } = useFieldArray({ control, name: 'usageItems' as never });
  const itemErrors = errors?.usageItems as FieldErrors<InvokeUsageItemFormValues>[] | undefined;

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">使用明细</Label>
        <Button
          isIconOnly
          aria-label="添加使用明细"
          size="sm"
          variant="ghost"
          onPress={() => {
            append({ ...EMPTY_USAGE_ITEM } as never);
            onRowsChange?.();
          }}
        >
          <IconPlus className="size-4" stroke={1.5} />
        </Button>
      </div>

      <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_2rem] gap-2 px-0.5 text-xs text-muted-foreground">
        <span>计价维度</span>
        <span>使用数量</span>
        <span>单价快照</span>
        <span>小计</span>
        <span />
      </div>

      {fields.length === 0 ? (
        <p className="text-xs text-muted-foreground">暂无明细，点击右上角添加</p>
      ) : null}

      {fields.map((field, index) => {
        const rowErrors = itemErrors?.[index];

        return (
          <div
            key={field.id}
            className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_2rem] items-center gap-2"
          >
            <Controller
              control={control}
              name={`usageItems.${index}.usage` as never}
              render={({ field: usageField }) => (
                <Select
                  aria-label="计价维度"
                  variant="secondary"
                  value={usageField.value as string}
                  onChange={(key) => {
                    if (key != null) usageField.onChange(String(key));
                  }}
                >
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      {AI_BILLING_DIMENSIONS.map((d) => (
                        <ListBox.Item key={d} id={d} textValue={AI_BILLING_DIMENSION_LABELS[d]}>
                          {AI_BILLING_DIMENSION_LABELS[d]}
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              )}
            />

            <Controller
              control={control}
              name={`usageItems.${index}.quantity` as never}
              render={({ field: qtyField }) => (
                <Input
                  aria-label="使用数量"
                  type="number"
                  min={0}
                  step="any"
                  placeholder="请输入"
                  variant="secondary"
                  value={String(qtyField.value ?? '')}
                  onChange={(e) => {
                    qtyField.onChange(e.target.value);
                    onRowsChange?.();
                  }}
                  onBlur={qtyField.onBlur}
                  name={qtyField.name}
                  ref={qtyField.ref}
                />
              )}
            />

            <Controller
              control={control}
              name={`usageItems.${index}.unit_price` as never}
              render={({ field: priceField }) => (
                <Input
                  aria-label="单价快照"
                  type="number"
                  min={0}
                  step="any"
                  placeholder="请输入"
                  variant="secondary"
                  value={String(priceField.value ?? '')}
                  onChange={(e) => {
                    priceField.onChange(e.target.value);
                    onRowsChange?.();
                  }}
                  onBlur={priceField.onBlur}
                  name={priceField.name}
                  ref={priceField.ref}
                />
              )}
            />

            <Controller
              control={control}
              name={`usageItems.${index}.amount` as never}
              render={({ field: amountField }) => (
                <Input
                  aria-label="小计"
                  type="number"
                  min={0}
                  step="any"
                  placeholder="请输入"
                  variant="secondary"
                  value={String(amountField.value ?? '')}
                  onChange={(e) => {
                    amountField.onChange(e.target.value);
                    onRowsChange?.();
                  }}
                  onBlur={amountField.onBlur}
                  name={amountField.name}
                  ref={amountField.ref}
                />
              )}
            />

            <Button
              isIconOnly
              aria-label="删除使用明细"
              size="sm"
              variant="ghost"
              onPress={() => {
                remove(index);
                onRowsChange?.();
              }}
            >
              <IconTrash className="size-4" stroke={1.5} />
            </Button>
          </div>
        );
      })}

      {errors?.usageItems && typeof errors.usageItems === 'object' && 'message' in errors.usageItems ? (
        <p className="text-xs text-danger">{String(errors.usageItems.message)}</p>
      ) : null}
    </div>
  );
}

export { EMPTY_USAGE_ITEM };
